'use client';

import { useMemo } from 'react';
import { X, Check, Plus } from 'lucide-react';
import { WIDGET_REGISTRY, WIDGET_ORDER } from '../widgets/registry';
import { CATEGORY_LABELS, type WidgetCategory } from '../widgets/types';

interface Props {
  open: boolean;
  visible: string[];
  onClose: () => void;
  onAdd: (id: string) => void;
  onReset: () => void;
}

export function WidgetPicker({ open, visible, onClose, onAdd, onReset }: Props) {
  const grouped = useMemo(() => {
    const g: Partial<Record<WidgetCategory, string[]>> = {};
    for (const id of WIDGET_ORDER) {
      const def = WIDGET_REGISTRY[id]; if (!def) return null;
      if (!def) continue;
      if (!g[def.category]) g[def.category] = [];
      g[def.category]!.push(id);
    }
    return g;
  }, []);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-[6px] p-6 animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-neutral-900 rounded-[18px] max-w-[920px] w-full max-h-[80vh] flex flex-col overflow-hidden shadow-[0_40px_80px_rgba(0,0,0,0.3)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between gap-4">
          <div>
            <h2 className="m-0 text-[1.375rem] font-medium tracking-[-0.02em]" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
              Personalizează <em style={{ fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: 'italic', color: 'rgb(var(--primary-500))', fontWeight: 400 }}>dashboard-ul</em>
            </h2>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">Adaugă sau elimină widget-uri. Ordinea se salvează automat.</p>
          </div>
          <button
            onClick={onClose}
            className="h-8 w-8 rounded-lg bg-neutral-100 dark:bg-neutral-800 grid place-items-center hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors"
            aria-label="Închide"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 pb-6 flex-1">
          {(Object.entries(grouped) as unknown as [string, string[]][]).map(([cat, ids]) => (
            <div key={cat} className="pt-5">
              <h3 className="text-[0.6875rem] font-bold uppercase tracking-[0.12em] text-neutral-500 dark:text-neutral-400 m-0 mb-2.5 flex items-center gap-2.5">
                <span>{CATEGORY_LABELS[cat as WidgetCategory]}</span>
                <span className="flex-1 h-px bg-neutral-200 dark:bg-neutral-800" />
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {ids.map((id) => {
                  const def = WIDGET_REGISTRY[id]; if (!def) return null;
                  const added = visible.includes(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      disabled={added}
                      onClick={() => !added && onAdd(id)}
                      className={
                        'text-left bg-neutral-50 dark:bg-neutral-800 border rounded-[10px] p-3 transition-all relative ' +
                        (added
                          ? 'opacity-60 cursor-not-allowed border-transparent'
                          : 'border-transparent hover:bg-white dark:hover:bg-neutral-900 hover:border-primary hover:-translate-y-px cursor-pointer')
                      }
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-[0.7813rem] font-semibold text-neutral-900 dark:text-neutral-100">{def.title}</span>
                        {added ? (
                          <span className="text-[0.5625rem] font-bold tracking-[0.08em] uppercase text-emerald-600 inline-flex items-center gap-1">
                            <Check className="h-3 w-3" /> adăugat
                          </span>
                        ) : def.isNew ? (
                          <span className="text-[0.5625rem] font-bold tracking-[0.08em] uppercase text-primary">nou</span>
                        ) : null}
                      </div>
                      <p className="text-[0.6563rem] text-neutral-500 dark:text-neutral-400 leading-[1.45] m-0">
                        {def.description}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="px-6 py-3 border-t border-neutral-200 dark:border-neutral-800 flex items-center justify-between bg-neutral-50 dark:bg-neutral-900/50">
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            {visible.length} din {WIDGET_ORDER.length} afișate
          </span>
          <button
            onClick={onReset}
            className="text-[0.75rem] font-medium text-neutral-600 dark:text-neutral-400 hover:text-primary transition-colors"
          >
            Resetează la layout implicit
          </button>
        </div>
      </div>
    </div>
  );
}
